/**
 * THE analysis-report allowance each plan includes, for every surface that
 * quotes it.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * A published allowance is a TERM OF SALE, not marketing copy. We have already
 * shipped the failure this file prevents: a customer bought a plan advertised
 * as "매월 200건" and the grant path silently handed them 2 a month, every
 * month, for a year. The postmortem
 * (.claude/TODO/Credit-manage-analysis-9-23.md §10, line 752) calls delivering
 * less than the advertised amount "a mis-sale, not a fraud control".
 *
 * Before this file the number was typed out by hand in two different places --
 * inside feature strings on /pricing -- and NOT stated at all on the '/' pricing
 * section, which is the surface most buyers actually read. Hand-typed in three
 * places is how the advertised number and the granted number drift apart.
 *
 * HOW IT IS WORDED -- A SERVICE WITH A FIXED QUANTITY, NOT A STORED VALUE
 * ----------------------------------------------------------------------
 * The customer-facing unit is "분석 리포트" (short labels: "리포트"), counted in
 * 건 and PROVIDED (제공) -- "analysis reports" in English. KG이니시스 reviews
 * stored-value wording (the word for "credit", top-up, balance, points) as
 * 환금성, a cash-equivalent good on a separate and stricter merchant-review
 * track (.claude/TODO/Portone-KGinisys-Pricing-fix-10-2.md §1, §3). The
 * wording is also simply the truth: one analysis produces one report the
 * customer keeps, nothing is sold standalone, and nothing has cash value.
 * The never-say list and the allowed exceptions are §3.2-§3.3 of that plan.
 *
 * The NAMES below still say "credits" on purpose. They are internal
 * identifiers that tests and the backend tether look up by name, and they
 * mirror the server's own (`monthly_credits`, `credits_remaining`). Rename the
 * words customers read, never the identifiers.
 *
 * ⚠ THE AUTHORITATIVE NUMBER IS NOT HERE.
 * It is backend/app/services/product_catalogue.py -- `monthly_credits` on each
 * plan SKU, read at grant time by plan_credit_service.monthly_credits_for_tier.
 * That is what ACTUALLY lands in organizations.credits_remaining. This file is
 * a MIRROR so the browser can render it without a round trip.
 *
 * THE TETHER: backend/tests/test_plan_credits_match_frontend.py parses this file
 * and fails if any number here disagrees with the catalogue, if a sellable plan
 * SKU is missing from the map, if the published term disagrees with a plan's
 * `service_period_months`, or if this file moves. It does not skip -- a silent
 * skip is how the mirror rots. If that test fails, the CATALOGUE WINS: fix this
 * file, never the catalogue, unless the business actually changed the product.
 */

/**
 * Analysis reports provided at EACH plan-month anniversary, keyed by
 * membership tier.
 *
 * Hard reset, not cumulative (product_catalogue Q3): a Starter org has 200
 * reports available at the start of every plan month whether or not it used
 * last month's. That is why every rendering of these numbers has to say
 * "매월" / "each month" -- see `monthlyCreditsLine` -- and why a 12-month
 * total may only ever appear beside it -- see `planTermTotalLine`.
 *
 * ⚠ TIERS ARE ABSENT ON PURPOSE. Trial, Enterprise and the retired tiers
 * (studio, fgi_studio) have NO plan SKU, so `monthly_credits_for_tier` returns
 * None for them and there is no number to mirror. A missing key means "we
 * cannot state an allowance for this tier", and every call site must render
 * nothing rather than a zero -- an unknown allowance and an empty one are
 * different facts (incident O3, components/OrgCreditPanel).
 */
export const PLAN_MONTHLY_CREDITS = Object.freeze({
  starter: 200,
  pro: 600,
});

/**
 * Analysis reports a brand-new organisation receives ON SIGNUP. ONE TIME, NOT
 * MONTHLY.
 *
 * ⚠ DO NOT MERGE THIS INTO `PLAN_MONTHLY_CREDITS`. It is a different kind of
 * fact and it comes from a different place on the server: api/auth.py seeds
 * `organizations.credits_remaining = 5` at org creation, sized by
 * core/config.default_trial_credits. There is no Trial plan SKU and no
 * anniversary grant, so a trial org gets 5 once and never again.
 *
 * The '/' pricing card used to advertise Trial as "월 검증 건수 5건" -- 5 per
 * MONTH -- which is the same class of overstatement as Starter's "무제한", just
 * smaller. Anything rendering this must say 1회 / one time.
 */
export const TRIAL_SIGNUP_CREDITS = 5;

/**
 * The one phrasing for a monthly allowance, shared by '/', '/pricing',
 * checkout and Settings.
 *
 * Takes the number as an ARGUMENT rather than reading the map itself. That is
 * deliberate and it is what makes the guard in
 * landing/__tests__/planCredits.test.js meaningful: the call site looks the
 * number up in `PLAN_MONTHLY_CREDITS`, so the test can swap the map and watch
 * the rendered figure follow. A helper that closed over the map would keep
 * rendering the real number under a mock and the test would prove nothing.
 * (SettingsPage passes a locale-formatted string; that works here, but not in
 * `planTermTotalLine`, which multiplies.)
 *
 * ⚠ "매월" IS LOAD-BEARING, NOT DECORATION. On '/' this line sits next to
 * "월 환산 · 12개월 이용료 일괄 결제" on a product that bills ₩2,388,000 up
 * front. A bare "분석 리포트 200건" there reads as 200 for the YEAR -- a 12x
 * understatement of what the buyer gets, published next to a live card
 * checkout.
 */
export const monthlyCreditsLine = (credits, isKo) =>
  isKo ? `매월 분석 리포트 ${credits}건 제공` : `${credits} analysis reports each month`;

/**
 * The one phrasing for the signup grant. See `TRIAL_SIGNUP_CREDITS` for why
 * the "1회" / "one time" qualifier is not optional.
 */
export const signupCreditsLine = (credits, isKo) =>
  isKo ? `가입 시 분석 리포트 ${credits}건 무료 제공 (1회)` : `${credits} free analysis reports on signup (one time)`;

/** Plan term. Mirrors product_catalogue `service_period_months` (12); tethered by
 *  backend/tests/test_plan_credits_match_frontend.py. */
export const PLAN_TERM_MONTHS = 12;

/** "12개월간 총 N건". ALWAYS render it next to monthlyCreditsLine, never alone: the
 *  monthly grant is a hard reset, not a 12-month pool. Takes a NUMBER.
 *
 *  KG이니시스 asked to see the service as a fixed quantity over the term
 *  ("12개월 총 N건 분석 리포트 제공"). The no-carry-over clause is part of the
 *  line, not an optional suffix: "총 2,400건" alone promises 2,400 usable at
 *  once, which the monthly reset makes false. */
export const planTermTotalLine = (monthly, isKo, months = PLAN_TERM_MONTHS) => {
  const total = (Number(monthly) * months).toLocaleString(isKo ? 'ko-KR' : 'en-US');
  return isKo
    ? `${months}개월간 총 ${total}건 제공 · 미사용분은 다음 달로 이월되지 않음`
    : `${total} in total over ${months} months · unused reports do not carry over`;
};
